import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { Loader2, Clock, CheckCircle2, PackageOpen, ShoppingBag, PoundSterling, ScrollText, ArrowLeft, ArrowLeftRight, RotateCcw, ExternalLink, Send, Pencil, X, MapPin, Truck, Landmark } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { formatGBP, clothingSummary } from "@/lib/marketplace";
import { useKidAuth } from "@/context/KidAuthContext";
import ListingBadges from "@/components/ListingBadges";
import EditListingModal from "@/components/EditListingModal";
import TransferStatusPill from "@/components/TransferStatusPill";
import { subscribeWalletUpdates, publishWalletUpdate } from "@/lib/walletBus";
import { subscribeListingUpdates, mergeListingInto } from "@/lib/listingBus";

/**
 * Iteration 78 — Kid Zone Ledger page (mirror of Parent Ledger).
 *
 * Seven tabs in exact master-list order, kid-friendly copy + kid endpoints:
 *   1. Pending Approvals  → items awaiting parent review + rejection feedback
 *   2. Live Listings      → active shop
 *   3. Reserved Items     → "Sent to Parent" badge + Release button
 *   4. Orders             → items sold vs items bought
 *   5. Earnings           → pocket-money tracker
 *   6. History Ledger     → read-only friendly activity log
 *   7. Transfers          → cash-outs + allowance top-ups (mirrors parent)
 */

const TABS = [
  { key: "pending",  label: "Pending",  icon: Clock,         testid: "kid-ledger-tab-pending" },
  { key: "live",     label: "Live",     icon: CheckCircle2,  testid: "kid-ledger-tab-live" },
  { key: "reserved", label: "Reserved", icon: PackageOpen,   testid: "kid-ledger-tab-reserved" },
  // Iteration 166 — Orders tab now redirects to the canonical
  // `/orders` Sales & Purchases page. Every other kid ledger tab
  // stays local.
  { key: "orders",   label: "Orders",   icon: ShoppingBag,   testid: "kid-ledger-tab-orders", href: "/orders" },
  { key: "earnings", label: "Earnings", icon: PoundSterling, testid: "kid-ledger-tab-earnings" },
  { key: "history",  label: "History Listings",  icon: ScrollText,    testid: "kid-ledger-tab-history" },
  // Iteration 282 — dedicated Transfers tab (mirrors the parent ledger).
  { key: "transfers", label: "Transfers", icon: ArrowLeftRight, testid: "kid-ledger-tab-transfers" },
];

const Empty = ({ label }) => (
  <div className="rounded-3xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500" data-testid="kid-ledger-empty">
    {label}
  </div>
);

function TabHeader({ active, onChange, kid }) {
  // Iteration 175 — Unified Orders spec. Orders tab redirects to the
  // single master /orders view carrying `?kid=<id>` so the
  // destination surfaces the correct kid back-link.
  const ordersHref = kid?.kid_id
    ? `/orders?kid=${encodeURIComponent(kid.kid_id)}`
    : "/orders";
  return (
    <div className="flex flex-wrap gap-2 mb-5" data-testid="kid-ledger-tabs">
      {TABS.map((t) => {
        const Icon = t.icon;
        const isActive = t.key === active;
        const cls = `inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold border transition ${
          isActive
            ? "bg-brandPink text-white border-brandPink shadow-sm"
            : "bg-white text-slate-700 border-slate-200 hover:border-brandPink"
        }`;
        // Iteration 166 — Orders tab redirects instead of swapping local state.
        if (t.href) {
          const href = t.key === "orders" ? ordersHref : t.href;
          return (
            <Link key={t.key} to={href} className={cls} data-testid={t.testid}>
              <Icon className="w-3.5 h-3.5" /> {t.label}
            </Link>
          );
        }
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={cls}
            data-testid={t.testid}
          >
            <Icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        );
      })}
    </div>
  );
}

function ListingList({ listings, mode, onRelease, releasingId, onEdit, testidPrefix }) {
  if (!listings.length) return <Empty label="Nothing to show here yet." />;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid={`${testidPrefix}-list`}>
      {listings.map((l) => (
        <div key={l.id} className="rounded-3xl border border-slate-200 bg-white p-4 flex flex-col gap-2" data-testid={`${testidPrefix}-row-${l.id}`}>
          <div className="flex items-start justify-between gap-2">
            <div className="font-display text-base font-medium text-slate-900 line-clamp-2">{l.title}</div>
            <div className="text-brandPinkDark font-semibold whitespace-nowrap">{formatGBP(l.price)}</div>
          </div>
          <ListingBadges listing={l} only={["status", "condition", "clothing", "parcel", "parent_posted"]} />
          {mode === "reserved" && (
            <div className="inline-flex items-center gap-1 rounded-full bg-amber-100 border border-amber-200 text-amber-900 text-[11px] font-bold px-2 py-0.5 w-fit" data-testid={`kid-sent-to-parent-${l.id}`}>
              <Send className="w-3 h-3" /> Sent to Parent
            </div>
          )}
          {mode === "pending" && l.rejection_reason && (
            <div className="rounded-2xl bg-rose-50 border border-rose-200 px-3 py-2 text-[12px] text-rose-900" data-testid={`kid-rejection-${l.id}`}>
              <strong>Parent&rsquo;s feedback:</strong> {l.rejection_reason}
            </div>
          )}
          <div className="flex items-center gap-2 pt-1">
            <Link
              to={`/listing/${l.id}`}
              className="inline-flex items-center gap-1 text-xs font-bold text-brandPinkDark hover:underline"
              data-testid={`${testidPrefix}-open-${l.id}`}
            >
              Open <ExternalLink className="w-3 h-3" />
            </Link>
            {mode === "live" && onEdit && (
              <button
                type="button"
                onClick={() => onEdit(l)}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-slate-100 text-slate-800 border border-slate-200 hover:bg-slate-200 transition"
                data-testid={`${testidPrefix}-edit-${l.id}`}
              >
                <Pencil className="w-3 h-3" /> Edit
              </button>
            )}
            {mode === "reserved" && onRelease && (
              <button
                type="button"
                onClick={() => onRelease(l.id)}
                disabled={releasingId === l.id}
                className="ml-auto inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-900 border border-amber-200 hover:bg-amber-200 disabled:opacity-60"
                data-testid={`${testidPrefix}-release-${l.id}`}
              >
                {releasingId === l.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                Release Back
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// Iteration 175 — Unified Orders spec: the Kid Zone Shop Ledger no
// longer renders an inline orders table. The Orders tab is a direct
// redirect to `/orders?kid=<id>` (handled in `TabHeader` as a router
// `<Link>` and defensively re-enforced in the `useEffect` below when a
// user deep-links `?tab=orders`). Any legacy `scope=self` /
// kid-scoped fetch has been removed.

function EarningsTab({ stats, wallet, onOpenEscrow, pendingCashoutPence = 0, onTransfer, listings = [] }) {
  // Iteration 96 — the Earnings ledger tab must render the SAME sale-only
  // figure as the Earnings stat card. Previously this used
  // `wallet?.wallet_pennies` first — which caused a mismatch and leaked
  // top-ups / allowance deposits into the "money earned from sales"
  // display. Backend `kid_stats.total_earnings_pennies` is the canonical
  // source (filtered by `_KID_KIND_SALE`). We still surface the wallet
  // balance below as a secondary line so the kid can see what's actually
  // spendable, but the headline is strictly sales.
  //
  // Iteration 173 — Escrow Hold split. `total_earnings_pennies` still
  // includes rows in the "paid" bucket; we subtract `escrow_hold_pennies`
  // for the "settled sales" headline and render the escrow line in its
  // own metrics block so kids can see money that's ringfenced pending
  // shipment confirmation. The escrow block is a button that opens a
  // drawer listing the underlying paid-not-shipped orders.
  const totalEarningsPence = stats?.total_earnings_pennies ?? 0;
  const escrowPence = stats?.escrow_hold_pennies ?? 0;
  const settledEarningsPence = Math.max(totalEarningsPence - escrowPence, 0);
  const walletPence = wallet?.wallet_pennies ?? 0;
  // Iteration 276 — pending cash-out requests lock those funds from the
  // spendable figure until the parent approves/declines.
  const spendablePence = Math.max(walletPence - pendingCashoutPence, 0);
  const escrowClickable = escrowPence > 0 && typeof onOpenEscrow === "function";

  // Iteration 309 — the numbered, date-sorted completed-sales breakdown
  // lives HERE under "Total earnings from sales" (moved out of History
  // Listings, which is back to a clean inventory table). Newest first,
  // matching the passbook sequence.
  const soldRows = useMemo(
    () => (listings || []).filter((l) => (l.status || "").toLowerCase() === "sold"),
    [listings],
  );
  const saleDateOf = (l) => l.sold_at || l.updated_at || l.created_at || "";
  const sortedSoldRows = useMemo(
    () => [...soldRows].sort((a, b) => saleDateOf(b).localeCompare(saleDateOf(a))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [soldRows],
  );
  const fmtSaleDate = (iso) => {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
  };
  return (
    <div className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6" data-testid="kid-earnings-card">
      {/* Iteration 309 — strict financial stack order: Spendable (top,
          ready to use) → Escrow (middle, pending shipment) → Total
          Earnings from Sales (bottom, with the itemised breakdown). */}

      {/* 1) SPENDABLE WALLET BALANCE — top */}
      <div data-testid="kid-earnings-section-spendable">
        <div className="text-[11px] font-bold uppercase tracking-wider text-emerald-800">Spendable wallet balance</div>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="text-2xl font-display font-semibold text-emerald-900 mt-0.5" data-testid="kid-wallet-spendable">{formatGBP(spendablePence / 100)}</div>
          {/* Iteration 276 — Child cash-out (Option A): the kid asks to
              move money to the verified parent household account; the
              parent approves with their PIN before anything moves. */}
          <button
            type="button"
            onClick={onTransfer}
            disabled={spendablePence <= 0}
            className="btn-primary !py-2.5 !px-4 text-sm inline-flex items-center gap-2 disabled:opacity-50"
            data-testid="kid-transfer-to-parent-btn"
          >
            <Landmark className="w-4 h-4" /> Transfer to Parent
          </button>
        </div>
        {pendingCashoutPence > 0 && (
          <div className="text-[11px] font-bold text-amber-700 mt-1" data-testid="kid-cashout-pending-note">
            {formatGBP(pendingCashoutPence / 100)} waiting for your parent to approve
          </div>
        )}
        <div className="text-[11px] text-emerald-700/80 mt-1">Includes released sales, allowance, and pocket money top-ups ready to spend.</div>
      </div>

      {/* 2) ESCROW HOLD — middle */}
      <button
        type="button"
        onClick={escrowClickable ? onOpenEscrow : undefined}
        disabled={!escrowClickable}
        className={`mt-4 pt-4 border-t border-emerald-200 block w-full text-left transition ${escrowClickable ? "hover:bg-amber-50/50 rounded-2xl -mx-2 px-2 cursor-pointer" : "cursor-default"}`}
        data-testid="kid-escrow-hold-block"
        aria-label={escrowClickable ? "View escrow orders" : "Escrow hold summary"}
      >
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800">Escrow hold</div>
          {escrowClickable && (
            <span className="text-[11px] font-bold text-amber-700 inline-flex items-center gap-1">
              View items <ExternalLink className="w-3 h-3" />
            </span>
          )}
        </div>
        <div className="text-2xl font-display font-semibold text-amber-900 mt-0.5" data-testid="kid-escrow-hold">{formatGBP(escrowPence / 100)}</div>
        <div className="text-[11px] text-amber-800/90 mt-1">Money from pending sales waiting for item shipment confirmation.</div>
      </button>

      {/* 3) TOTAL EARNINGS FROM SALES — bottom, with the numbered,
          date-sorted itemised breakdown (moved from History, iteration
          309). Headline stays the settled (post-escrow) figure. */}
      <div className="mt-4 pt-4 border-t border-emerald-200" data-testid="kid-earnings-section-total">
        <Link
          to="/kidzone/ledger?tab=history"
          className="block group rounded-2xl -mx-2 px-2 py-1 transition hover:bg-emerald-100/60"
          data-testid="kid-earnings-total-link"
          aria-label="View your sold items in the full history"
        >
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-xs font-bold uppercase tracking-wider text-emerald-900 inline-flex items-center gap-1">
              Total earnings from sales <ExternalLink className="w-3 h-3 opacity-60 group-hover:opacity-100" />
            </div>
            {soldRows.length > 0 && (
              <span
                className="inline-flex items-center px-2.5 py-0.5 rounded-full bg-emerald-600 text-white text-[10px] font-bold uppercase tracking-wider"
                data-testid="kid-history-earnings-count"
              >
                {soldRows.length} item{soldRows.length === 1 ? "" : "s"} sold
              </span>
            )}
          </div>
          <div className="text-4xl font-display font-semibold text-emerald-900 mt-1 group-hover:text-emerald-700 transition" data-testid="kid-earnings-total">{formatGBP(settledEarningsPence / 100)}</div>
          <div className="text-[12px] text-emerald-800 mt-1">Money earned from toys you&rsquo;ve sold. Top-ups from your parent are excluded.</div>
        </Link>
        {soldRows.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2" data-testid="kid-history-earnings-items">
            {sortedSoldRows.map((l, i) => (
              <Link
                key={l.id}
                to={`/listing/${l.id}`}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white border border-emerald-200 text-xs font-semibold text-slate-800 hover:border-emerald-400 transition whitespace-nowrap"
                data-testid={`kid-history-sold-item-${l.id}`}
              >
                <span className="font-mono font-bold text-emerald-600">{i + 1}.</span>
                <span>{l.title}</span>
                {fmtSaleDate(saleDateOf(l)) && (
                  <span className="text-slate-400">({fmtSaleDate(saleDateOf(l))})</span>
                )}
                <span className="font-mono font-bold text-emerald-700">— {formatGBP(l.price ?? 0)}</span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Iteration 276 — Cash-out modal: pick an amount or transfer everything.
// Submitting only creates a PENDING request — no money moves until the
// parent approves with their household PIN (banking safety: kid accounts
// can never link to external banks; funds route via the verified parent
// household account only).
function CashoutModal({ open, onClose, availablePence, onSubmitted }) {
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (open) { setAmount(""); setErr(""); setBusy(false); }
  }, [open]);

  if (!open) return null;
  const pence = Math.round((parseFloat(amount) || 0) * 100);
  const valid = pence > 0 && pence <= availablePence;

  const submit = async (p) => {
    setBusy(true); setErr("");
    try {
      await api.post("/kid/wallet/cashout-requests", { amount_pennies: p });
      publishWalletUpdate({ reason: "cashout-request" });
      onSubmitted?.();
      onClose();
    } catch (e) {
      setErr(formatApiError(e) || "Couldn't send that request right now.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4" data-testid="kid-cashout-modal" onClick={busy ? undefined : onClose}>
      <div className="card-base w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3">
          <div className="font-display text-lg font-semibold text-brandSlate">Transfer to Parent</div>
          <button type="button" onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600" data-testid="kid-cashout-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-xs text-slate-600 mb-4 leading-relaxed">
          Ask your parent to move money from your wallet into the household account. Nothing moves until they approve it.
        </p>
        <label className="block text-xs font-bold text-slate-600 mb-1" htmlFor="kid-cashout-amount">Amount (£)</label>
        <input
          id="kid-cashout-amount"
          type="number" min="0.01" step="0.01" inputMode="decimal"
          className="input-base"
          placeholder="0.00"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          data-testid="kid-cashout-amount"
        />
        <button
          type="button"
          onClick={() => setAmount((availablePence / 100).toFixed(2))}
          className="mt-2 text-xs font-bold text-brandPink hover:underline"
          data-testid="kid-cashout-transfer-all"
        >
          Transfer All ({formatGBP(availablePence / 100)})
        </button>
        {err && <div className="text-xs font-bold text-rose-700 mt-2" data-testid="kid-cashout-error">{err}</div>}
        <button
          type="button"
          disabled={!valid || busy}
          onClick={() => submit(pence)}
          className="btn-primary w-full mt-4 disabled:opacity-50"
          data-testid="kid-cashout-submit"
        >
          {busy ? "Sending…" : `Request ${valid ? formatGBP(pence / 100) : "transfer"}`}
        </button>
      </div>
    </div>
  );
}

// Iteration 173 — Escrow drawer. Slides in from the right on desktop and
// slides up on mobile so the kid can review every paid-not-shipped
// order that's holding money in escrow. Fetches on open, retries on
// close-then-reopen so freshly-paid orders show up without a page
// refresh.
function EscrowDrawer({ open, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true); setErr("");
    (async () => {
      try {
        const { data } = await api.get("/kid/escrow-orders");
        if (!cancelled) setRows(data?.orders || []);
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e) || "Couldn't load escrow orders right now.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const fmt = (r) => {
    const bits = [r.address_line1, r.address_line2, r.city, r.postcode].filter(Boolean);
    if (bits.length) return bits.join(", ");
    const d = r.delivery_address || {};
    return [d.line1 || d.street, d.line2, d.city, d.postcode].filter(Boolean).join(", ");
  };

  const totalPence = rows.reduce((sum, r) => sum + Number(r.sale_price_pennies || r.net_pennies || 0), 0);

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-[2px] flex items-end sm:items-stretch sm:justify-end"
      role="dialog"
      aria-modal="true"
      data-testid="escrow-drawer-overlay"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-md sm:h-full bg-white rounded-t-3xl sm:rounded-none sm:rounded-l-3xl shadow-2xl flex flex-col max-h-[85vh] sm:max-h-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-wider text-amber-800">Escrow hold</div>
            <div className="font-display text-xl font-semibold text-brandSlate">Waiting to be shipped</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 rounded-full inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 transition"
            data-testid="escrow-drawer-close"
            aria-label="Close escrow drawer"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="escrow-drawer-list">
          {loading ? (
            <div className="text-sm text-slate-500 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading orders…</div>
          ) : err ? (
            <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-2xl px-4 py-3">{err}</div>
          ) : rows.length === 0 ? (
            <div className="text-sm text-slate-500 text-center py-10" data-testid="escrow-drawer-empty">
              Nothing waiting in escrow right now.
            </div>
          ) : rows.map((r) => (
            <div key={r.id} className="rounded-2xl border border-amber-200 bg-amber-50/50 p-4" data-testid={`escrow-drawer-item-${r.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <Link to={`/orders/${r.id}`} className="font-bold text-slate-900 text-sm hover:text-brandTeal truncate block" data-testid={`escrow-drawer-title-${r.id}`}>
                    {r.title}
                  </Link>
                  <div className="text-[11px] text-slate-500 mt-0.5">Order #{(r.id || "").slice(0, 8)}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="font-display font-semibold text-amber-900" data-testid={`escrow-drawer-amount-${r.id}`}>
                    {formatGBP(Number(r.sale_price_pennies || r.net_pennies || 0) / 100)}
                  </div>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-amber-700 mt-0.5">{r.status || "paid"}</div>
                </div>
              </div>

              <div className="mt-3 space-y-1.5 text-[12px] text-slate-700">
                <div className="flex items-start gap-2">
                  <span className="font-semibold text-slate-500 shrink-0 w-16">Buyer</span>
                  <span>{r.buyer_name || r.recipient_name || r.buyer_kid_name || "Guest"}</span>
                </div>
                {fmt(r) && (
                  <div className="flex items-start gap-2">
                    <MapPin className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                    <span className="text-slate-600 break-words">{fmt(r)}</span>
                  </div>
                )}
                <div className="flex items-start gap-2">
                  <Truck className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
                  <span className="text-slate-600">
                    {r.fulfillment_method === "shipping" || r.fulfillment_method === "home_delivery"
                      ? "Royal Mail Post — post it and mark shipped to release funds"
                      : r.fulfillment_method === "school_gate"
                        ? "School-gate collection — mark collected to release funds"
                        : r.fulfillment_method === "parent_handoff"
                          ? "Parent handoff — mark collected to release funds"
                          : "Mark the order shipped/collected to release funds"}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {rows.length > 0 && (
          <div className="border-t border-slate-100 px-5 py-3 flex items-center justify-between text-sm" data-testid="escrow-drawer-total">
            <span className="text-slate-600">{rows.length} {rows.length === 1 ? "order" : "orders"} in escrow</span>
            <span className="font-display font-semibold text-amber-900">{formatGBP(totalPence / 100)}</span>
          </div>
        )}
      </div>
    </div>
  );
}

// Iteration 283 — TransferStatusPill is now the shared component in
// components/TransferStatusPill.jsx (used here and by My passbook).

// Iteration 282 — kid-facing Transfers tab (mirrors the parent layout):
// every cash-out request and allowance top-up in one clean table.
function KidTransfersTab({ cashouts = [] }) {
  const [topups, setTopups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const { data } = await api.get("/kid/ledger?limit=100");
        const txns = Array.isArray(data?.transactions) ? data.transactions : [];
        // Allowance / pocket-money rows only — cash-outs are tracked by
        // the requests feed, so to_parent passbook rows are excluded to
        // avoid showing the same transfer twice.
        if (!cancel) setTopups(txns.filter((r) => r.kind_group === "from_parent"));
      } catch {
        if (!cancel) setTopups([]);
      } finally {
        if (!cancel) setLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, []);

  const rows = useMemo(() => {
    const out = [];
    for (const r of cashouts || []) {
      out.push({
        key: `cashout-${r.id}`,
        type: "Transfer to Parent",
        amountGBP: (r.amount_pennies || 0) / 100,
        credit: false,
        date: r.created_at || "",
        cashout: r,
        destination: "Parent Household - Cash-Out",
      });
    }
    for (const t of topups || []) {
      out.push({
        key: `topup-${t.id}`,
        type: "Allowance from Parent",
        amountGBP: Math.abs(t.amount_pennies ?? t.delta_pennies ?? 0) / 100,
        credit: true,
        date: t.created_at || "",
        cashout: null,
        destination: "My Wallet",
      });
    }
    out.sort((a, b) => (b.date || "").localeCompare(a.date || ""));
    return out;
  }, [cashouts, topups]);

  if (loading) {
    return <Loader2 className="w-5 h-5 animate-spin text-slate-500 mx-auto" data-testid="kid-transfers-loading" />;
  }
  if (!rows.length) {
    return <Empty label="No transfers yet — your top-ups and cash-outs will show up here." />;
  }
  return (
    <div className="rounded-3xl border border-slate-200 bg-white overflow-x-auto" data-testid="kid-transfers-tab">
      <table className="w-full text-sm" data-testid="kid-transfers-table">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
          <tr>
            <th className="text-left px-4 py-3 font-bold">Type / Action</th>
            <th className="text-right px-4 py-3 font-bold">Amount</th>
            <th className="text-left px-4 py-3 font-bold">Date</th>
            <th className="text-left px-4 py-3 font-bold">Status</th>
            <th className="text-left px-4 py-3 font-bold">Destination</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key} className="border-t border-slate-100" data-testid={`kid-transfers-row-${r.key}`}>
              <td className="px-4 py-2.5 font-semibold text-slate-900" data-testid={`kid-transfers-type-${r.key}`}>{r.type}</td>
              <td className={`px-4 py-2.5 text-right font-semibold whitespace-nowrap ${r.credit ? "text-emerald-700" : "text-rose-700"}`} data-testid={`kid-transfers-amount-${r.key}`}>
                {r.credit ? "+" : "-"}{formatGBP(r.amountGBP)}
              </td>
              <td className="px-4 py-2.5 text-[12px] text-slate-600 whitespace-nowrap" data-testid={`kid-transfers-date-${r.key}`}>
                {(r.date || "").slice(0, 10) || "—"}
              </td>
              <td className="px-4 py-2.5">
                {r.cashout ? (
                  <TransferStatusPill row={r.cashout} testid={`kid-transfers-status-${r.key}`} />
                ) : (
                  <span className="inline-flex items-center px-2.5 py-1 rounded-full border text-[11px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border-emerald-200" data-testid={`kid-transfers-status-${r.key}`}>
                    Completed
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 text-[13px] text-slate-700" data-testid={`kid-transfers-destination-${r.key}`}>{r.destination}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryListingsTab({ listings, kidName }) {
  const rows = useMemo(() => {
    // Iteration 211 — hide rejected / deleted listings from the kid's
    // History Ledger so it stays a clean archive of meaningful
    // activity. Matches the Parent Ledger filter (iteration 206) so
    // both surfaces stay in lockstep.
    const REJECTED_STATUSES = new Set([
      "rejected", "rejected_parent", "rejected_admin",
      "removed", "deleted",
    ]);
    const isRejected = (s) => {
      const status = (s || "").toLowerCase();
      if (REJECTED_STATUSES.has(status)) return true;
      return status.startsWith("rejected_")
        || status.startsWith("deleted_")
        || status.startsWith("removed_");
    };
    const filtered = (listings || []).filter((l) => !isRejected(l.status));
    const sorted = [...filtered];
    sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return sorted;
  }, [listings]);

  // Iteration 309 — the earnings banner moved INTO the Earnings tab;
  // History Listings is back to a clean, unencumbered inventory table.

  if (!rows.length) return <Empty label="No toys yet — every toy you list will show up here." />;

  const statusClass = (s) => {
    const status = (s || "").toLowerCase();
    if (["live", "public", "approved", "active"].includes(status)) return "bg-emerald-100 text-emerald-800 border-emerald-200";
    if (status === "reserved") return "bg-amber-100 text-amber-900 border-amber-200";
    if (["sold", "completed"].includes(status)) return "bg-sky-100 text-sky-800 border-sky-200";
    if (["rejected", "removed", "deleted"].includes(status)) return "bg-rose-100 text-rose-800 border-rose-200";
    return "bg-slate-100 text-slate-700 border-slate-200";
  };

  // Iteration 282 — cash-out pills moved to the module-scope shared
  // TransferStatusPill (History card + Transfers tab stay in sync).

  // Iteration 143 — History Listings now renders the canonical 6th
  // Seller column so its schema matches Orders / Earnings tabs and the
  // Parent Dashboard's History Listings surface. Every listing belongs
  // to the active Kid Zone session, so seller_name = kidName for all
  // rows.
  return (
    <div className="space-y-4">
      {/* Iteration 283 — the Household Balance Transfers card no longer
          leaks into History Listings; transfers render strictly under
          the dedicated Transfers tab. */}
      {rows.length > 0 && (
    <div className="rounded-3xl border border-slate-200 bg-white overflow-x-auto" data-testid="kid-history-listings">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-600">
          <tr>
            <th className="text-left px-4 py-3 font-bold">Item</th>
            <th className="text-left px-4 py-3 font-bold">Title</th>
            <th className="text-right px-4 py-3 font-bold">Price</th>
            <th className="text-left px-4 py-3 font-bold">Posted</th>
            <th className="text-left px-4 py-3 font-bold">Status</th>
            <th className="text-left px-4 py-3 font-bold" data-testid="kid-history-header-seller">Seller</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((l) => {
            const thumb = (l.photos && l.photos[0]) || null;
            return (
              <tr key={l.id} className="border-t border-slate-100" data-testid={`kid-history-listing-row-${l.id}`}>
                <td className="px-4 py-2">
                  {thumb ? (
                    <img src={thumb} alt="" className="w-12 h-12 rounded-xl object-cover border border-slate-200" data-testid={`kid-history-listing-thumb-${l.id}`} />
                  ) : (
                    <div className="w-12 h-12 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-400 text-[10px]">No photo</div>
                  )}
                </td>
                <td className="px-4 py-2 text-slate-900 font-medium">
                  <Link to={`/listing/${l.id}`} className="hover:underline" data-testid={`kid-history-listing-title-${l.id}`}>{l.title}</Link>
                  {clothingSummary(l) && (
                    <div className="text-[11px] font-bold text-rose-700 mt-0.5" data-testid={`kid-history-clothing-summary-${l.id}`}>{clothingSummary(l)}</div>
                  )}
                </td>
                <td className="px-4 py-2 text-right text-brandPinkDark font-semibold whitespace-nowrap">{formatGBP(l.price)}</td>
                <td className="px-4 py-2 text-[12px] text-slate-600 whitespace-nowrap">{(l.created_at || "").slice(0, 10) || "—"}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${statusClass(l.status)}`} data-testid={`kid-history-listing-status-${l.id}`}>
                    {l.status || "—"}
                  </span>
                </td>
                <td className="px-4 py-2 text-[12px] text-slate-700 whitespace-nowrap font-medium" data-testid={`kid-history-listing-seller-${l.id}`}>
                  {kidName || "—"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
      )}
    </div>
  );
}

export default function KidLedger() {
  const { kid } = useKidAuth();
  const [params, setParams] = useSearchParams();
  const initialTab = params.get("tab") || "pending";
  const [active, setActive] = useState(TABS.some((t) => t.key === initialTab) ? initialTab : "pending");
  // Iteration 307 — in-page deep-links (e.g. the Earnings headline →
  // ?tab=history) must switch the tab even when already on this route.
  useEffect(() => {
    const t = params.get("tab");
    if (t && TABS.some((x) => x.key === t) && t !== active) setActive(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);
  const [stats, setStats] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [listings, setListings] = useState([]);
  const [siblings, setSiblings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState("");
  const [releasingId, setReleasingId] = useState("");
  const [editingListing, setEditingListing] = useState(null);
  // Iteration 173 — Escrow drawer visibility. Kept at the page level so
  // the modal renders above every tab and survives tab switches.
  const [escrowOpen, setEscrowOpen] = useState(false);
  // Iteration 276 — child cash-out requests (Option A).
  const [cashouts, setCashouts] = useState([]);
  const [cashoutOpen, setCashoutOpen] = useState(false);

  const refreshCashouts = useCallback(async () => {
    if (!kid) return;
    try {
      const { data } = await api.get("/kid/wallet/cashout-requests");
      setCashouts(data?.requests || []);
    } catch { /* keep prior state */ }
  }, [kid?.kid_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { refreshCashouts(); }, [refreshCashouts]);

  const pendingCashoutPence = useMemo(
    () => cashouts.filter((r) => r.status === "pending").reduce((s, r) => s + (r.amount_pennies || 0), 0),
    [cashouts],
  );
  const cashoutAvailablePence = Math.max((wallet?.wallet_pennies ?? 0) - pendingCashoutPence, 0);

  // Iteration 173 — Lightweight refetch of the two feeds that drive the
  // Earnings tab (stats + wallet). Kept outside the main effect so
  // window focus / visibility handlers can poke it without
  // re-triggering the full listings pull.
  const refreshStats = useCallback(async () => {
    if (!kid) return;
    try {
      const [s, w] = await Promise.all([
        api.get("/kid/stats").catch(() => ({ data: null })),
        api.get("/kid/wallet").catch(() => ({ data: null })),
      ]);
      setStats(s.data);
      setWallet(w.data);
    } catch { /* keep prior state */ }
  }, [kid?.kid_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!kid) return;
    (async () => {
      setLoading(true);
      try {
        const [s, w, l] = await Promise.all([
          api.get("/kid/stats").catch(() => ({ data: null })),
          api.get("/kid/wallet").catch(() => ({ data: null })),
          api.get("/kid/listings"),
        ]);
        setStats(s.data);
        setWallet(w.data);
        setListings(l.data || []);
        // Iteration 140 — fetch household roster so the Orders tab
        // can render sibling scope pills. Silently no-op when no
        // parent cookie is present (kid signed in alone).
        try {
          const { data: fam } = await api.get("/parent/family-wallet");
          const roster = (fam?.kids || [])
            .map((k) => ({ id: k.id, name: (k.name || k.label || "").split(" (")[0] }))
            .filter((k) => k.id);
          setSiblings(roster);
        } catch { setSiblings([]); }
      } catch (e) {
        setToast(formatApiError(e) || "Couldn't load your ledger.");
      } finally {
        setLoading(false);
      }
    })();
  }, [kid?.kid_id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Iteration 87 — auto-refresh stats + wallet + listings whenever a
  // sale completes or a parent deposit lands, so the passbook and the
  // running balance stay live without a manual reload.
  useEffect(() => {
    return subscribeWalletUpdates(() => {
      if (!kid) return;
      (async () => {
        try {
          const [s, w, l] = await Promise.all([
            api.get("/kid/stats").catch(() => ({ data: null })),
            api.get("/kid/wallet").catch(() => ({ data: null })),
            api.get("/kid/listings").catch(() => ({ data: [] })),
          ]);
          setStats(s.data);
          setWallet(w.data);
          setListings(l.data || []);
        } catch { /* keep prior state */ }
      })();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kid?.kid_id]);

  // Iteration 89 — listing edits (kid's own PATCH or parent re-approval
  // in another tab) splice the fresh listing into the local grid so the
  // status/badges reflect the pending → live transition instantly.
  useEffect(() => {
    return subscribeListingUpdates((updated) => {
      setListings((prev) => mergeListingInto(prev, updated));
    });
  }, []);

  // Iteration 173 — Auto-refresh stats + wallet whenever the Kid Ledger
  // window/tab regains focus. Handles both `focus` (browser tab
  // returned to) and `visibilitychange` (mobile web-app comes out of
  // background) so escrow / earnings never lag behind a payment that
  // completed while the kid was elsewhere.
  useEffect(() => {
    if (!kid) return undefined;
    const onFocus = () => { refreshStats(); };
    const onVisible = () => {
      if (document.visibilityState === "visible") refreshStats();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [kid?.kid_id, refreshStats]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeTab = (key) => {
    setActive(key);
    const next = new URLSearchParams(params);
    next.set("tab", key);
    setParams(next, { replace: true });
  };

  const releaseListing = async (listingId) => {
    setReleasingId(listingId);
    try {
      await api.post(`/parent/listings/${listingId}/force-release`);
      const { data: fresh } = await api.get("/kid/listings");
      setListings(fresh || []);
      setToast("Released — the item is back on the marketplace.");
    } catch (e) {
      setToast(formatApiError(e) || "Couldn't release right now.");
    } finally {
      setReleasingId("");
    }
  };

  const norm = (s) => (s || "").toLowerCase();
  // Iteration 211 — drop rejected/deleted listings from the Pending
  // tab. Historically `rejected` was left inside so kids could see
  // parent feedback, but the new data-hygiene spec keeps Pending
  // strictly for items that are actually pending review — rejected
  // items belong in History (and are ALSO hidden there now).
  const isRejectedOrDeleted = (s) => {
    const status = norm(s);
    if (["rejected", "removed", "deleted"].includes(status)) return true;
    return status.startsWith("rejected_")
      || status.startsWith("deleted_")
      || status.startsWith("removed_");
  };
  const pendingListings  = useMemo(
    () => listings.filter(
      (l) => !isRejectedOrDeleted(l.status)
        && (norm(l.status).startsWith("pending") || norm(l.status) === "awaiting_approval"),
    ),
    [listings], // eslint-disable-line react-hooks/exhaustive-deps -- isRejectedOrDeleted is a stable module-scope predicate
  );
  const liveListings     = useMemo(() => listings.filter((l) => ["live", "approved", "active", "public"].includes(norm(l.status))), [listings]);
  const reservedListings = useMemo(() => listings.filter((l) => norm(l.status) === "reserved"), [listings]);

  // Iteration 175 — Unified Orders spec. Deep-linking `?tab=orders`
  // redirects to the canonical `/orders?kid=<id>` master view instead
  // of rendering the retired kid-scoped inline table. Kept AFTER every
  // hook declaration to preserve the rules of hooks.
  if (initialTab === "orders") {
    const q = kid?.kid_id ? `?kid=${encodeURIComponent(kid.kid_id)}` : "";
    return <Navigate to={`/orders${q}`} replace />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-brandPink/5 via-white to-brandTeal/5 py-8 px-4" data-testid="kid-ledger-page">
      <div className="max-w-5xl mx-auto">
        <Link to="/kidzone/workspace" className="inline-flex items-center gap-1 text-sm text-slate-600 hover:text-brandPinkDark mb-4" data-testid="kid-ledger-back">
          <ArrowLeft className="w-4 h-4" /> Back to your shop
        </Link>
        <h1
          className="text-3xl md:text-4xl font-display font-semibold text-slate-900 mb-2"
          data-testid="kid-ledger-heading"
        >
          {(kid?.name || kid?.kid_name)
            ? `${kid.name || kid.kid_name}'s Shop Ledger`
            : "My Shop Ledger"}
        </h1>
        <p className="text-sm text-slate-600 mb-5">Track everything your shop is doing — approvals, live toys, orders, and pocket money.</p>

        <TabHeader active={active} onChange={changeTab} kid={kid} />

        {toast && (
          <div className="mb-4 rounded-2xl bg-slate-900 text-white px-4 py-2 text-sm inline-block" data-testid="kid-ledger-toast">
            {toast}
          </div>
        )}

        {loading ? (
          <Loader2 className="w-6 h-6 animate-spin text-slate-500 mx-auto" />
        ) : (
          <>
            {active === "pending"  && <ListingList listings={pendingListings}  mode="pending"  testidPrefix="kid-pending" />}
            {active === "live"     && <ListingList listings={liveListings}     mode="live"     onEdit={setEditingListing} testidPrefix="kid-live" />}
            {active === "reserved" && (
              <ListingList
                listings={reservedListings}
                mode="reserved"
                onRelease={releaseListing}
                releasingId={releasingId}
                testidPrefix="kid-reserved"
              />
            )}
            {active === "earnings" && <EarningsTab stats={stats} wallet={wallet} listings={listings} onOpenEscrow={() => setEscrowOpen(true)} pendingCashoutPence={pendingCashoutPence} onTransfer={() => setCashoutOpen(true)} />}
            {active === "history"  && <HistoryListingsTab listings={listings} kidName={kid?.name || kid?.kid_name} />}
            {active === "transfers" && <KidTransfersTab cashouts={cashouts} />}
          </>
        )}
      </div>

      <EscrowDrawer
        open={escrowOpen}
        onClose={() => { setEscrowOpen(false); refreshStats(); }}
      />

      <CashoutModal
        open={cashoutOpen}
        onClose={() => setCashoutOpen(false)}
        availablePence={cashoutAvailablePence}
        onSubmitted={() => { refreshCashouts(); refreshStats(); }}
      />

      {editingListing && (
        <EditListingModal
          listing={editingListing}
          onClose={() => setEditingListing(null)}
          onSaved={async (updated) => {
            setEditingListing(null);
            // Kid edits force parent re-approval — updated listing comes
            // back with status=pending_parent_approval so it disappears
            // from Live and reappears in the Pending tab automatically.
            if (updated && updated.id) {
              setListings((prev) => prev.map((l) => (l.id === updated.id ? { ...l, ...updated } : l)));
            } else {
              try {
                const { data: fresh } = await api.get("/kid/listings");
                setListings(fresh || []);
              } catch { /* keep prior list */ }
            }
            setToast("Your updates have been sent to your parent for re-approval!");
            setTimeout(() => setToast(""), 4000);
          }}
        />
      )}
    </div>
  );
}
